import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Autocomplete,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  GolfPropertyEnum,
  SnCPropertyEnum,
  SpaPropertyEnum,
} from "../../../constants/NavbarList";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const ELIGIBLE_FOR_COST_PLUS = "Eligible for Cost Plus";
const ELIGIBLE_FOR_IN_HOUSE_SERVICE_CHARGE =
  "Eligible for In House Service Charge";

const parseYesNoFlag = (value: unknown): boolean =>
  value === true || value === "Yes" || value === "True" || value === "true";

const RetailCategories = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [categoryGroup, setCategoryGroup] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [eligibleForDiscount, setEligibleForDiscount] =
    useState<boolean>(false);
  const [eligibleForCostOrCostPlus, setEligibleForCostOrCostPlus] =
    useState<boolean>(false);
  const [
    eligibleForInHouseServiceChargeCalculation,
    setEligibleForInHouseServiceChargeCalculation,
  ] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameError2, setNameError2] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [GolfRetailCategory, setGolfRetailCategory] = useState<any>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [categoryGroupList, setCategoryGroupList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    if (!fieldValues) {
      return;
    }
    const categories =
      fieldValues[GolfPropertyEnum.retailCategories] ??
      fieldValues[SpaPropertyEnum.retailCategories] ??
      fieldValues[SnCPropertyEnum.retailCategories];
    setGolfRetailCategory(Array.isArray(categories) ? categories : []);
    const categoryGroupData =
      fieldValues[GolfPropertyEnum.categoryGroups] ??
      fieldValues[SpaPropertyEnum.categoryGroups] ??
      fieldValues[SnCPropertyEnum.categoryGroups];
    if (categoryGroupData && Array.isArray(categoryGroupData)) {
      setCategoryGroupList(
        fillAutocompleteValues(
          categoryGroupData,
          DNDFieldsEnum.CATEGORYGROUP,
          "",
        ),
      );
    }
  }, []);

  const validateInput = () => {
    setNameError(false);
    setNameError2(false);

    const trimmedCategoryGroup = String(categoryGroup ?? "").trim();
    if (
      trimmedCategoryGroup &&
      !validateString(
        categoryGroup,
        validationLength.categoryGroup,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      setAlertMessage(validationMessages.categoryGroup);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (!categoryName || String(categoryName).trim() === "") {
      setNameError2(true);
      setAlertMessage("Category Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        categoryName,
        validationLength.golfCategoryName,
        validationType.NAME,
      )
    ) {
      setNameError2(true);
      setAlertMessage(validationMessages.golfCategoryName);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const updateGolfRetailCategory = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        GolfRetailCategory.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.CATEGORYNAME,
        categoryName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setNameError2(false);
    setGolfRetailCategory((prev: any) => {
      return Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...payload,
              };
            }
            return item;
          })
        : prev;
    });
    editGolfRetailCategory(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${GolfPropertyEnum.retailCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfRetailCategory = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        GolfRetailCategory,
        DNDFieldsEnum.CATEGORYNAME,
        categoryName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfRetailCategory((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length > 0
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfRetailCategory(null);
    setEditClicked(false);
  };

  const editGolfRetailCategory = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setCategoryGroup(null);
      setCategoryName(null);
      setEligibleForDiscount(false);
      setEligibleForCostOrCostPlus(false);
      setEligibleForInHouseServiceChargeCalculation(false);
      setActive(true);
      setNameError(false);
      setNameError2(false);
      return;
    }
    list?.id != null && setSelectedId(list.id);
    setEditClicked(true);
    setCategoryGroup(list?.["Category Group"] ?? null);
    setCategoryName(list?.["Category Name"] ?? null);
    setEligibleForDiscount(
      list?.["Eligible for Discount"] === true ||
        list?.["Eligible for Discount"] === "Yes",
    );
    setEligibleForCostOrCostPlus(parseYesNoFlag(list?.[ELIGIBLE_FOR_COST_PLUS]));
    setEligibleForInHouseServiceChargeCalculation(
      parseYesNoFlag(list?.[ELIGIBLE_FOR_IN_HOUSE_SERVICE_CHARGE]),
    );
    setActive(list?.Active !== false && list?.Active !== "No");
    setNameError(false);
    setNameError2(false);
  };

  const deleteGolfRetailCategory = (id: number) => {
    setGolfRetailCategory(deleteItem(GolfRetailCategory, id));
  };

  useEffect(() => {
    data(GolfRetailCategory);
    editClicked &&
      setSelectedId(
        checkData(GolfRetailCategory, selectedId) ? selectedId : null,
      );
  }, [GolfRetailCategory]);

  const payload = {
    "Category Group":
      categoryGroup && String(categoryGroup).trim() !== ""
        ? categoryGroup
        : null,
    "Category Name": categoryName,
    "Eligible for Discount": eligibleForDiscount,
    [ELIGIBLE_FOR_COST_PLUS]: eligibleForCostOrCostPlus,
    [ELIGIBLE_FOR_IN_HOUSE_SERVICE_CHARGE]:
      eligibleForInHouseServiceChargeCalculation,
    Active: active,
  };
  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Stack direction="row" width="100%" justifyContent="space-between">
              <Typography variant="h6" component="div" sx={textStyle}>
                Retail Categories
                <Tooltip
                  title={tooltipComponent(
                    golfTooltipHeaders.retailCategoryHeaders,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Stack direction="row" gap="10px" justifyContent="space-between">
                {editClicked && (
                  <ClearButton
                    click={() => {
                      editGolfRetailCategory(null);
                      setSelectedId(null);
                    }}
                  />
                )}
                {lastUpdatedRecord && (
                  <CopyButton
                    click={() => {
                      editGolfRetailCategory(lastUpdatedRecord);
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="20px">
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                id="categoryGroup"
                disablePortal
                options={removeSampleTestArray(categoryGroupList)}
                sx={{ width: "100%" }}
                value={categoryGroup}
                disabled={!categoryGroupList?.length}
                onChange={(_e, newValue) => {
                  setCategoryGroup(newValue ?? null);
                  setNameError(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category Group"
                    variant="outlined"
                    error={nameError}
                    helperText={
                      nameError ? validationMessages.categoryGroup : ""
                    }
                  />
                )}
              />
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.categoryGroup)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Category Name *
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.categoryName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="CategoryName"
                variant="outlined"
                placeholder="Category Name"
                value={categoryName ?? ""}
                helperText={
                  nameError2
                    ? !categoryName || String(categoryName).trim() === ""
                      ? validationMessages.fieldRequired
                      : validationMessages.categoryName
                    : ""
                }
                error={nameError2}
                onChange={(e: any) => {
                  setCategoryName(e.target.value || null);
                  setNameError2(false);
                }}
                sx={{ width: "100%" }}
              />
            </Stack>
          </Stack>

          <Stack
            className="form-checkbox-row"
            direction="row"
            alignItems="center"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(golfTooltipHeaders.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>

          <Stack
            className="form-checkbox-row form-checkbox-row--three"
            direction="row"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Checkbox
                    id="eligibleForDiscount"
                    checked={eligibleForDiscount}
                    onChange={(e) => setEligibleForDiscount(e.target.checked)}
                  />
                }
                label="Eligible for Discounts"
              />
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.eligibleForDiscount)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Checkbox
                    id="eligibleForCostOrCostPlus"
                    checked={eligibleForCostOrCostPlus}
                    onChange={(e) =>
                      setEligibleForCostOrCostPlus(e.target.checked)
                    }
                  />
                }
                label="Eligible for Cost Plus"
              />
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.eligibleForCostOrCostPlus,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Checkbox
                    id="eligibleForInHouseServiceChargeCalculation"
                    checked={eligibleForInHouseServiceChargeCalculation}
                    onChange={(e) =>
                      setEligibleForInHouseServiceChargeCalculation(
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Eligible for In House Service Charge"
              />
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.eligibleForInHouseServiceChargeCalculation,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId
                  ? updateGolfRetailCategory()
                  : addGolfRetailCategory()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Retail Category
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Retail Categories Available</Typography>
            <Stack direction="column" gap="10px">
              {GolfRetailCategory ? (
                <DraggableItemList
                  itemData={GolfRetailCategory}
                  onOrderUpdate={(order) => setGolfRetailCategory(order)}
                  onEdit={(info) => editGolfRetailCategory(info)}
                  onDelete={(id) => deleteGolfRetailCategory(id)}
                  code={DNDFieldsEnum.CATEGORYGROUP}
                  name={DNDFieldsEnum.CATEGORYNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Retail Category Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </Stack>
  );
};

export default RetailCategories;
